import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const COOKIE_NAME = "emrkt_ref";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90; // 90 days
const ATTRIBUTION_KEY = "emrkt-attribution-v1";

function setRefCookie(code: string) {
  try {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(code)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
  } catch {
    // ignore
  }
}

function persistAttribution(code: string, params: URLSearchParams) {
  try {
    const existing = window.localStorage.getItem(ATTRIBUTION_KEY);
    if (existing) return; // first-touch wins
    const payload: Record<string, string> = {
      referral_code: code,
      landing_path: "/r/" + code,
      referrer: document.referrer?.slice(0, 400) || "",
      first_seen_at: new Date().toISOString(),
    };
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
      const v = params.get(k);
      if (v) payload[k] = v.slice(0, 200);
    }
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function safeDest(raw: string | null): string {
  if (!raw) return "/";
  // Only allow same-origin paths to prevent open-redirect.
  if (raw.startsWith("/") && !raw.startsWith("//")) return raw;
  return "/";
}

export default function ReferralRedirect() {
  const { code: rawCode } = useParams();
  const [params] = useSearchParams();

  useEffect(() => {
    const code = (rawCode ?? "").toUpperCase().slice(0, 32);
    const dest = safeDest(params.get("to") ?? params.get("dest"));

    if (code) {
      setRefCookie(code);
      persistAttribution(code, params);
      // Fire-and-forget click log.
      supabase.functions
        .invoke("referral-click", {
          body: {
            code,
            dest,
            referer: document.referrer || null,
            utm_source: params.get("utm_source"),
            utm_medium: params.get("utm_medium"),
            utm_campaign: params.get("utm_campaign"),
          },
        })
        .catch(() => {});
    }

    // Forward whitelisted UTM params on the redirect so downstream pages see them.
    const passthrough = new URLSearchParams();
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
      const v = params.get(k);
      if (v) passthrough.set(k, v);
    }
    if (code) passthrough.set("ref", code);

    const sep = dest.includes("?") ? "&" : "?";
    const target = passthrough.toString() ? `${dest}${sep}${passthrough.toString()}` : dest;

    // Small delay so the click POST has a moment to flush before navigation.
    const t = window.setTimeout(() => {
      window.location.replace(target);
    }, 150);
    return () => window.clearTimeout(t);
  }, [rawCode, params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
      Redirecting…
    </div>
  );
}